import styled from "@emotion/styled";
import { doctorLoginPage, toDoctorCreateAccountPage } from "Doctor/Router";
import { homePage } from "Home/routes";
import { privacyPolicyURL, termsOfUseURL } from "Policies";
import { useStore } from "effector-react";
import { memo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import {
  hasDigit,
  hasLowercaseCharacter,
  hasMoreThan8Symbols,
  hasSpecialCharacter,
  hasUppercaseCharacter,
} from "shared-functions/strongPasswordValidation";
import {
  Block,
  Button,
  Checkbox,
  Flex,
  Grid,
  HeroBanner,
  Input,
  LoaderWithOverlay,
  Logo,
  PasswordInput,
  Typography,
  UnifiedLink,
  color,
  errorNotification,
} from "ui-kit";
import { signUp } from "./doctorSession";
import loginBg from "./login-bg.jpg";

type Credentials = {
  email: string;
  password: string;
  passwordConfirmation: string;
  isAgree: boolean;
};

export const RegistrationPage = memo(() => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<Credentials>();
  const isLoading = useStore(signUp.pending);

  const password = watch("password");

  const registerDoctor: SubmitHandler<Credentials> = useCallback(
    ({ email, password }) => {
      if (isLoading) {
        return;
      }
      signUp({ email, password })
        .then(({ id: doctorId }) => {
          navigate(toDoctorCreateAccountPage(doctorId));
        })
        .catch((error: Error) => {
          errorNotification(error.message);
        });
    },
    [navigate, isLoading]
  );

  return (
    <Grid gridTemplateColumns="30% 70%" minHeight="100vh">
      <Helmet>
        <title>Doctor sign up</title>
      </Helmet>
      <LoginFormContainer flexDirection="column" justifyContent="space-between">
        {isLoading && <LoaderWithOverlay />}
        <RegistrationForm>
          <Block textAlign="left">
            <Link to={homePage}>
              <Logo fill={color.G200} width="188px" />
            </Link>
          </Block>
          <Flex alignItems="center">
            <LoginFormSection width="100%">
              <Block marginBottom={3} textAlign="center">
                <Typography as="h1" type="h2">
                  Sign up
                </Typography>
              </Block>
              <form onSubmit={handleSubmit(registerDoctor)}>
                <Block marginBottom={3}>
                  <Input
                    autoFocus
                    type="email"
                    hasErrors={!!errors["email"]}
                    errorMessage={errors["email"]?.message}
                    label="Email"
                    placeholder="Email"
                    isRequired
                    {...register("email", {
                      required: "Please, enter email",
                    })}
                  />
                </Block>
                <Block marginBottom={3}>
                  <PasswordInput
                    label="Password"
                    hasErrors={!!errors["password"]}
                    errorMessage={errors["password"]?.message}
                    placeholder="Password"
                    enableSecurityCheck
                    isRequired
                    {...register("password", {
                      validate: (password) => isPasswordValid(password),
                    })}
                  />
                </Block>
                <Block marginBottom={3}>
                  <PasswordInput
                    label="Password confirmation"
                    hasErrors={!!errors["passwordConfirmation"]}
                    errorMessage={errors["passwordConfirmation"]?.message}
                    placeholder="Password confirmation"
                    isRequired
                    {...register("passwordConfirmation", {
                      validate: (confirmedPassword) =>
                        (isPasswordValid(password)
                          ? confirmedPassword === password
                          : true) || "Password doesn't match",
                    })}
                  />
                </Block>
                <Block marginBottom={4}>
                  <Checkbox
                    errorMessage={errors["isAgree"]?.message}
                    label={
                      <>
                        I agree to the{" "}
                        <UnifiedLink
                          target="_blank"
                          type="inherit"
                          to={termsOfUseURL}
                        >
                          terms of use
                        </UnifiedLink>{" "}
                        and{" "}
                        <UnifiedLink
                          target="_blank"
                          type="inherit"
                          to={privacyPolicyURL}
                        >
                          privacy policy
                        </UnifiedLink>
                      </>
                    }
                    {...register("isAgree", {
                      required:
                        "You have to agree with the terms of use and privacy policy",
                    })}
                  />
                </Block>
                <Block textAlign="center">
                  <Button filled>Sign up</Button>
                </Block>
              </form>
            </LoginFormSection>
          </Flex>
          <Block>
            <Typography type="h4">Already have an account?</Typography>{" "}
            <UnifiedLink to={doctorLoginPage} type="h4">
              Log in
            </UnifiedLink>
          </Block>
        </RegistrationForm>
      </LoginFormContainer>
      <Background enableOverlay image={loginBg}>
        <AdvantagesSection
          gridTemplateColumns="100%"
          gridTemplateRows="max-content auto max-content"
          height="100%"
        >
          <AdvantagesListContainer
            gridTemplateColumns="repeat(4, 1fr)"
            gridColumnGap={10}
            paddingLeft={3}
          >
            <Advantage type="h2" fontColor="N0">
              Security and privacy
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Easy to use system
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Flexible schedule
            </Advantage>
            <Advantage type="h2" fontColor="N0">
              Earnings as in the clinic
            </Advantage>
          </AdvantagesListContainer>
          <Invitation>
            Sign up now and help patients start and maintain a healthy lifestyle
            and be happy!
          </Invitation>
          <Disclaimer paddingLeft={6}>
            <Typography fontSize="20px" fontColor="N0" fontWeight={400}>
              The doctor must remember that the patient trusts you with his
              life, so the patient&apos;s life is in your hands, and you are
              responsible for it.
            </Typography>
          </Disclaimer>
        </AdvantagesSection>
      </Background>
    </Grid>
  );
});

function isPasswordValid(password: string = "") {
  return (
    hasLowercaseCharacter(password) &&
    hasUppercaseCharacter(password) &&
    hasSpecialCharacter(password) &&
    hasDigit(password) &&
    hasMoreThan8Symbols(password)
  );
}

const AdvantagesSection = styled(Grid.withComponent("section"))`
  align-content: space-between;
  padding: 72px 56px 32px 72px;
`;

const AdvantagesListContainer = styled(Grid)`
  grid-column: 1 / end;
  grid-row: 1 / 1;
`;

const Advantage = styled(Typography)`
  align-self: center;
  padding-left: 32px;
  position: relative;
  &::before {
    border-radius: 50%;
    background-color: ${color.G200};
    content: "";
    display: inline-block;
    left: 0;
    margin-top: -10px;
    top: 50%;
    position: absolute;
    height: 20px;
    width: 20px;
  }
`;

const Invitation = styled(Block)`
  align-self: center;
  color: ${color.N0};
  font-family: Roboto, Arial, sans-serif;
  font-size: 50px;
  font-weight: 700;
  grid-column: 1 / end;
  grid-row: 2 / 3;
  line-height: 72px;
  padding-left: 48px;
`;

const Disclaimer = styled(Block)`
  grid-column: 1 / end;
  grid-row: 3 / 3;
`;

const Background = styled(HeroBanner)`
  height: 100%;
  .overlay {
    opacity: 0.62;
  }
`;

const LoginFormContainer = styled(Flex)`
  background-color: ${color.N0};
  position: relative;
  width: 100%;
`;

const RegistrationForm = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 72px 72px 32px;
  width: 100%;
`;

const LoginFormSection = Block.withComponent("section");
